/**
 * How a captured page is measured, for both places a page is drawn: the Studio canvas
 * (src/capture/hotspotAnchor.ts, which reads the page directly) and the Preview / published view
 * (src/capture/replayFrame.ts, the script that runs inside the player's sandboxed frame).
 *
 * This file is the only copy. The player's script is bundled from it by
 * scripts/gen-replay-script.mjs, so the Studio and the player cannot pick a different element or
 * scroller for the same page: they run the same code. That is what keeps a hotspot or callout in
 * the same place in Preview as in the Studio. Keep it free of imports from the app (the bundle is
 * inlined into every scrollable replay) and of anything a browser in the player couldn't run.
 */

/** A box in the page's own CSS px, in the frame's viewport. */
export type Box = { left: number; top: number; right: number; bottom: number };

const styleOf = (d: Document, el: Element) => (d.defaultView ?? window).getComputedStyle(el);

/** Does `el` hold (nearly all of) the box? Then the box is on `el`, not on what `el` covers. */
export function covers(el: Element, box: Box): boolean {
  const r = el.getBoundingClientRect();
  const area = (box.right - box.left) * (box.bottom - box.top);
  if (!r.width || !r.height || area <= 0) return false;
  const w = Math.min(r.right, box.right) - Math.max(r.left, box.left);
  const h = Math.min(r.bottom, box.bottom) - Math.max(r.top, box.top);
  return w > 0 && h > 0 && (w * h) / area >= 0.9;
}

/**
 * Pick the best anchor element at (x, y): skip sticky/fixed elements so that a hotspot drawn over
 * content a sticky header merely overlaps anchors to the content, not the header — the box has to
 * scroll away with what it was drawn on.
 *
 * `box` is the hotspot's own box, and it decides the opposite case: a box that sits inside the
 * pinned bar was drawn on the bar, so the bar wins over the content behind it. Anchoring that one
 * to the content would slide it out from under the bar as soon as the page scrolled.
 *
 * Falls back to the topmost element (even if sticky) when nothing else is at that point.
 */
export function bestAnchorAt(d: Document, x: number, y: number, box?: Box): Element | null {
  const els = d.elementsFromPoint(x, y);
  let pinned: Element | null = null;
  for (const el of els) {
    if (el === d.documentElement || el === d.body) continue;
    const pos = styleOf(d, el).position;
    if (pos === "sticky" || pos === "fixed") { if (!pinned) pinned = el; continue; }
    return pinned && box && covers(pinned, box) ? pinned : el;
  }
  return pinned ?? d.elementFromPoint(x, y);
}

/**
 * The element a hotspot drawn at `box` is anchored to: the best one under the box's centre (see
 * bestAnchorAt). A box over bare page background anchors to <body>, so it still follows the page's
 * scroll. null = nothing can be hit-tested there (the centre is off the frame, or there is no layout yet).
 */
export function anchorFor(d: Document, box: Box): Element | null {
  const el = bestAnchorAt(d, (box.left + box.right) / 2, (box.top + box.bottom) / 2, box);
  return el === d.documentElement ? d.body : el;
}

/**
 * Where an anchor sits in the frame, in the page's own CSS px. <body> is special: when the page
 * scrolls on <body> itself its own box never moves (its children do), so its scroll offset stands
 * in for the movement. null = display:none or detached: no usable position.
 */
export function anchorPx(d: Document, el: Element): [number, number] | null {
  const r = el.getBoundingClientRect();
  if (!r.width && !r.height) return null;
  const isBody = el === d.body;
  return [r.left - (isBody ? d.body.scrollLeft : 0), r.top - (isBody ? d.body.scrollTop : 0)];
}

/**
 * The part of a page a "Moves with Page" callout scrolls with: the document if the page scrolls on its
 * own, otherwise the biggest scrolling container (an app with a fixed header and nav scrolls its
 * content in one). null = nothing on the page scrolls. The callout is at a fixed spot in that scroller's
 * content, so it comes into view as that scrolls and goes away again as it scrolls back.
 *
 * The document wins unless a container is strictly bigger; a container counts when it scrolls (overflow
 * auto/scroll/overlay with more content than room), and so does <body> when it clips more content than it
 * shows (the editor freezes overflow, so its overflow can't be read).
 */
export function pickScroller(d: Document): HTMLElement | null {
  const de = (d.scrollingElement as HTMLElement | null) ?? d.documentElement;
  const over = (el: Element) => el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1;
  let best: HTMLElement | null = null;
  let bestArea = 0;
  if (over(de)) { best = de; bestArea = de.clientWidth * de.clientHeight; }
  const els = d.body ? [d.body, ...Array.from(d.body.querySelectorAll<HTMLElement>("*"))] : [];
  for (const el of els) {
    if (el === de || /^(TEXTAREA|INPUT|SELECT)$/.test(el.tagName) || !over(el)) continue;
    const cs = styleOf(d, el);
    if (el !== d.body && !/auto|scroll|overlay/.test(cs.overflowY + " " + cs.overflowX)) continue;
    const area = el.clientWidth * el.clientHeight;
    if (area > bestArea) { best = el; bestArea = area; }
  }
  return best;
}

/** How far the page itself is scrolled (on <html>, or on <body> for a page that scrolls there). */
export function windowScroll(d: Document): [number, number] {
  const de = d.documentElement;
  return [de.scrollLeft || d.body?.scrollLeft || 0, de.scrollTop || d.body?.scrollTop || 0];
}

/**
 * Scroll the page itself (not an inner scroller) to where it was saved: on <html>, or on <body> for a page
 * that scrolls there. Both the Studio and the player restore a page's saved position with this.
 */
export function scrollWindowTo(d: Document, x: number, y: number) {
  const de = d.documentElement;
  if (d.defaultView) d.defaultView.scrollTo(x, y); else { de.scrollLeft = x; de.scrollTop = y; }
  if (y && de.scrollTop !== y && d.body) { d.body.scrollLeft = x; d.body.scrollTop = y; }
}

/** How far a scroller from `pickScroller` is scrolled, in the page's own px. */
export function scrollPos(d: Document, el: HTMLElement | null): [number, number] {
  if (!el) return [0, 0];
  const de = (d.scrollingElement as HTMLElement | null) ?? d.documentElement;
  return el === de ? windowScroll(d) : [el.scrollLeft, el.scrollTop];
}

/**
 * Where a page opens in the Studio and the player alike: its main scroller (from `pickScroller`) and the
 * page itself at the top. Any other inner scroller stays where it was saved. Instant, even on a page
 * whose own CSS asks for smooth scrolling.
 */
export function scrollToTop(d: Document, scroller: HTMLElement | null) {
  for (const el of [scroller, d.documentElement, d.body]) {
    if (!el) continue;
    try { el.scrollTo({ top: 0, left: 0, behavior: "instant" }); }
    catch { el.scrollTop = 0; el.scrollLeft = 0; }
  }
}

/**
 * Attached callouts (a callout with an arrow, shared/callout.ts): the element its arrow points at, found the way a
 * hotspot's is (anchorFor) at the point it was picked at, with the page's main scroller where it was then. The
 * Studio picks it when a callout is placed (calloutAnchorAt) and both the Studio and the player find it again from
 * what was saved (calloutElement); the arrow's tip is then a fixed distance from that element (calloutTip), so the card
 * follows it as the page scrolls, under a sticky header, in an inner scroller, at any window size.
 */
export type CalloutAnchorPx = { px: number; py: number; sx: number; sy: number; ox: number; oy: number };

/** The element at (x, y) a callout can be attached to: never the page itself (<html> or <body>), which is no element to
 *  follow: a callout over bare background stays at its spot on the page instead. */
function calloutElementAt(d: Document, x: number, y: number): Element | null {
  const el = anchorFor(d, { left: x - 0.5, top: y - 0.5, right: x + 0.5, bottom: y + 0.5 });
  return el && el !== d.body && el !== d.documentElement ? el : null;
}

/**
 * Attach a callout whose arrow's tip is at `tip` and was let go at `at` (both in the frame's viewport px, as the page is
 * now): the element under `at`, where the main scroller is now, and the tip's distance from that element. null =
 * nothing to attach to there. `snap` moves the tip given where the element is (shared/callout.ts, snapTip).
 */
export function calloutAnchorAt(
  d: Document, scroller: HTMLElement | null, at: [number, number], tip: [number, number],
  snap: (tip: [number, number], el: Box) => [number, number] = (t) => t,
): { anchor: CalloutAnchorPx; el: Element } | null {
  const el = calloutElementAt(d, at[0], at[1]);
  const p = el && anchorPx(d, el);
  if (!el || !p) return null;
  const r = el.getBoundingClientRect();
  const [tx, ty] = snap(tip, { left: r.left, top: r.top, right: r.right, bottom: r.bottom });
  const [sx, sy] = scrollPos(d, scroller);
  return { anchor: { px: at[0], py: at[1], sx, sy, ox: tx - p[0], oy: ty - p[1] }, el };
}

/**
 * Find an attached callout's element again: with the main scroller where it was when the callout was attached, the
 * element at the point it was attached at; then everything is put back. All in one task, so the browser never paints
 * the page scrolled there. null = nothing there now (it is shown at its x/y instead).
 */
export function calloutElement(d: Document, scroller: HTMLElement | null, a: CalloutAnchorPx): Element | null {
  const de = (d.scrollingElement as HTMLElement | null) ?? d.documentElement;
  const els = [de, d.documentElement, d.body, scroller].filter((el, i, all): el is HTMLElement => !!el && all.indexOf(el) === i);
  const was = els.map((el) => [el.scrollLeft, el.scrollTop] as const);
  const go = (el: HTMLElement, left: number, top: number) => {
    try { el.scrollTo({ left, top, behavior: "instant" }); } catch { el.scrollLeft = left; el.scrollTop = top; }
  };
  try {
    if (!scroller || scroller === de || scroller === d.body) scrollWindowTo(d, a.sx, a.sy);
    else go(scroller, a.sx, a.sy);
    return calloutElementAt(d, a.px, a.py);
  } finally {
    els.forEach((el, i) => go(el, was[i][0], was[i][1]));
  }
}

/** Where an attached callout's arrow points now, in the frame's viewport px. null = its element is gone or hidden. */
export function calloutTip(d: Document, el: Element, a: CalloutAnchorPx): [number, number] | null {
  const p = el.isConnected ? anchorPx(d, el) : null;
  return p && [p[0] + a.ox, p[1] + a.oy];
}
